import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FILTER_TYPE } from '../../models/filter-type';
import { KeyValue } from '../../models/key-value';
import { SelectAutocompleteService } from '../../services/select-autocomplete.service';

/**
 * The Select component.
 *
 * This select has 4 types of filter:
 *  By_value, by_key and by_value_or_code are managed by the component itself but the custom is managed by the parent component.
 */
@Component({
    selector: 'cw-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [
        SelectAutocompleteService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class SelectComponent implements OnInit, OnChanges {
    /**
     * The input data.
     * If the filter type is not custom, this data is used to filter.
     * If the filter type is custom, this data has to be filtered by the parent component
     */
    @Input() dataList: KeyValue[] = [];
    /** indicates the placeholder of the input */
    @Input() placeholder = '';
    /** indicates the minimum value for the search to start. By default is 3*/
    @Input() minValueLength = 3;
    /** indicates the filter type. By default is filter by value*/
    @Input() filterType = FILTER_TYPE.BY_VALUE;
    /** ngModel of selectedOption */
    @Input() selectedOption: KeyValue | null = null;
    /** indicates the placeholder of the search input. By default is 'Type here to start searching...'  */
    @Input() searchPlaceholder = 'Type here to start searching...';
    /** Shows a little description  below the input tag*/
    @Input() selectedOptionInfo = '';
    /** This param will emit value when the filter is custom
     * and the search has more than or equal number of characters than minValueLength param
     */
    @Output() searchTextChange = new EventEmitter<string | null>();

    toggleSelect = false;
    searchText: FormControl = new FormControl('');
    optionList: KeyValue[] = [];
    option: KeyValue | null = null;

    private notFound = false;

    // These methods are required because we are using Control Value Accessor
    /* eslint-disable */
    onChange = (value: KeyValue | null) => {};
    onTouch = () => {};
    /* eslint-enable */

    constructor(private selectService: SelectAutocompleteService) {}

    ngOnInit(): void {
        this.optionList = this.dataList;
    }

    ngOnChanges(): void {
        if (this.filterType === FILTER_TYPE.CUSTOM) {
            const notResultsFound = !this.dataList.length && this.searchText.value.length >= this.minValueLength;
            this.updateOptionList(this.dataList, notResultsFound);
        }
    }

    /** @ignore */
    onToggleSelect(): void {
        this.toggleSelect = !this.toggleSelect;
    }

    /** @ignore */
    writeValue(data: KeyValue | null): void {
        this.option = data;
        this.onChange(data);
    }

    /** @ignore */
    registerOnChange(fn: (value: KeyValue | null) => void): void {
        this.onChange = fn;
    }

    /** @ignore */
    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    /** @ignore */
    filterOptions(): void {
        if (this.searchText.value.length < this.minValueLength) {
            this.resetList();
            return;
        }

        if (this.filterType === FILTER_TYPE.CUSTOM) {
            this.searchTextChange.emit(this.searchText.value);
        } else {
            const result = this.selectService.filterOptions(this.dataList, this.filterType, this.searchText.value);
            this.updateOptionList(result, !result.length);
        }
    }

    /** @ignore */
    updateSelectedOption(value: KeyValue): void {
        this.writeValue(value);
        this.toggleSelect = false;
    }

    private updateOptionList(list: KeyValue[], notFound: boolean): void {
        this.optionList = list;
        this.notFound = notFound;
    }

    private resetList(): void {
        this.optionList = this.dataList;
        this.notFound = false;
    }
}
